import {
  ApiOutlined,
  BellOutlined,
  CameraOutlined,
  BulbOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  GatewayOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PrinterOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Empty,
  Layout,
  List,
  Menu,
  Modal,
  Popover,
  Space,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useUIStore } from "../../stores";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  extraActions?: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title = "Dashboard",
  extraActions,
}) => {
  const { logout, user } = useAuth();
  const {
    sidebarCollapsed,
    toggleSidebar,
    notifications,
    clearNotifications,
    removeNotification,
  } = useUIStore();

  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerVisible(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Menu items configuration
  const menuItems: MenuProps["items"] = [
    {
      key: "/",
      icon: <DashboardOutlined />,
      label: "Dashboard",
    },
    {
      key: "/devices",
      icon: <ApiOutlined />,
      label: "Devices",
      children: [
        {
          key: "/devices/tcp",
          label: "TCP Devices",
        },
        {
          key: "/devices/mqtt",
          label: "MQTT Devices",
        },
      ],
    },
    {
      key: "/scales",
      icon: <GatewayOutlined />,
      label: "Scales",
    },
    {
      key: "/pick-to-lights",
      icon: <BulbOutlined />,
      label: "Pick To Light",
    },
    {
      key: "/inkjet-printers",
      icon: <PrinterOutlined />,
      label: "Inkjet Printers",
    },
    {
      key: "/dataman-cameras",
      icon: <CameraOutlined />,
      label: "DataMan Cameras",
    },
    {
      key: "/audit-logs",
      icon: <FileTextOutlined />,
      label: "Audit Logs",
    },
    {
      key: "/redis",
      icon: <DatabaseOutlined />,
      label: "Redis Management",
    },
  ];

  // Handle menu click
  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // User dropdown menu
  const userMenuItems: MenuProps["items"] = [
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      danger: true,
      onClick: () => confirmLogout(),
    },
  ];

  const confirmLogout = () => {
    Modal.confirm({
      title: "Confirm Logout",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to log out of the system?",
      okText: "Logout",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: handleLogout,
      centered: true,
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = "/login";
    }
  };

  const notificationContent = (
    <div style={{ width: 300, maxHeight: 400, overflow: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Text strong>Notifications</Text>
        {notifications.length > 0 && (
          <Button type="link" size="small" onClick={clearNotifications}>
            Clear all
          </Button>
        )}
      </div>
      {notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No new notifications"
        />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  type="text"
                  size="small"
                  onClick={() => removeNotification(item.id)}
                >
                  ×
                </Button>,
              ]}
              style={{ padding: "12px 16px" }}
            >
              <List.Item.Meta
                avatar={
                  <Badge
                    status={
                      item.type === "error"
                        ? "error"
                        : item.type === "warning"
                        ? "warning"
                        : "success"
                    }
                  />
                }
                title={<Text style={{ fontSize: 13 }}>{item.message}</Text>}
                description={
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(item.timestamp).toLocaleString()}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const sidebarContent = (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          height: 64, // Match header height
          padding: "0 16px", // Remove vertical padding, rely on flex centering
          borderBottom: "1px solid #002140",
          background: "#001529",
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarCollapsed ? "center" : "flex-start",
          transition: "all 0.2s",
        }}
      >
        <Avatar
          size={sidebarCollapsed ? "default" : "large"}
          icon={<ApiOutlined />}
          shape="square"
          style={{
            backgroundColor: "#6366f1",
            marginRight: sidebarCollapsed ? 0 : 12,
            transition: "all 0.2s",
          }}
        />
        {!sidebarCollapsed && (
          <Title
            level={4}
            style={{
              color: "#fff",
              margin: 0,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            WCS Admin
          </Title>
        )}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={!sidebarCollapsed ? ["/devices"] : []}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ flex: 1, borderRight: 0, paddingTop: 8 }}
      />

      {/* User info removed as requested */}
    </div>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={sidebarCollapsed}
          width={260}
          style={{
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
            boxShadow: "2px 0 8px 0 rgba(29,35,41,.05)",
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0 }}
          width={260}
          closable
          headerStyle={{ display: "none" }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : sidebarCollapsed ? 80 : 260,
          transition: "all 0.2s",
          background: "#f8fafc", // Slightly lighter/cleaner background
        }}
      >
        <Header
          style={{
            padding: isMobile ? "0 12px" : "0 24px",
            background: "#fff",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 999,
            height: 64,
            boxShadow: "0 1px 4px rgba(0,21,41,.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: "0 1 auto" }}>
            {!isMobile && (
              <Button
                type="text"
                icon={
                  sidebarCollapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  )
                }
                onClick={toggleSidebar}
                style={{
                  fontSize: "16px",
                  width: 48,
                  height: 48,
                  marginRight: 8,
                  flexShrink: 0,
                }}
              />
            )}

            {isMobile && (
              <Button
                type="text"
                icon={<MenuFoldOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: "16px", marginRight: 8, flexShrink: 0 }}
              />
            )}

            <Title
              level={4}
              style={{
                margin: 0,
                color: "#1f2937",
                fontSize: isMobile ? "16px" : "20px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Title>
          </div>

          <Space size={isMobile ? "small" : "middle"} style={{ flexShrink: 0 }}>
            {!isMobile && extraActions}

            <Popover
              content={notificationContent}
              trigger="click"
              placement="bottomRight"
              overlayClassName="notification-popover"
            >
              <Badge count={notifications.length} size="small" offset={[-2, 2]}>
                <Button
                  type="text"
                  icon={
                    <BellOutlined
                      style={{ fontSize: "18px", color: "#64748b" }}
                    />
                  }
                  style={{ width: 40, height: 40, borderRadius: "50%" }}
                />
              </Badge>
            </Popover>

            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "4px 8px 4px 4px",
                  borderRadius: "24px",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s",
                  background: "#fff",
                  height: 40,
                }}
                className="user-dropdown-trigger"
              >
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: "#6366f1", marginRight: 8 }}
                />
                {!isMobile && (
                  <Space size={4}>
                    <span
                      style={{
                        color: "#1f2937",
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      {user?.username || "Admin"}
                    </span>
                    <DownOutlined
                      style={{ fontSize: "10px", color: "#94a3b8" }}
                    />
                  </Space>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: isMobile ? "12px" : "24px",
            padding: 0, // Let children handle padding or card wrappers
            minHeight: "calc(100vh - 112px)",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
